import type { MemberDataFirestoreRecord, Supplier } from "@albertine/types";
import { Route, useHistory, useRouteMatch } from "react-router";
import { useMemberRequests } from "../../common/hooks/useMemberRequests";
import type { Agent } from "../../common/types/Agent";
import type { Conversation } from "../../common/types/Conversation";
import useGlobalState from "../../state/GlobalStateHook";
import {
    defaultPanelUiState,
    type PanelUiState,
    panelUiStateKey,
} from "../../state/PanelUiState";
import routes, { type CatalogContext, routeSchemas } from "../../utils/routes";
import Catalog from "../CatalogBeta/Catalog";
import ProactivityCart from "../CatalogBeta/ShoppingCart/ProactivityCart";
import ViewCatalogItem from "../CatalogBeta/ViewCatalogItem";
import WithGoogle from "../CatalogBeta/WithGoogle";
import Chat from "../Chat/Chat";
import { createContextKey, getCurrentCatalogItems } from "../Chat/ChatContext";
import FullScreenOverlay from "../Common/Overlay/FullScreenOverlay";
import { MemberProfileHost } from "../MemberData/MemberProfileHost";
import MemberHeader from "../MemberHeader/MemberHeader";
import MemberRequests from "../RequestListView/MemberRequests";

type Props = {
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
    conversation: Conversation;
    suppliers: Supplier[];
};

const MemberWithRequestListView = function MemberWithRequestListView(props: Props) {
    const { currentAgent, member, suppliers, conversation } = props;
    const isArticlesRoute = useRouteMatch("/member/:memberId/articles/");
    const isArticleCategoriesRoute = useRouteMatch(
        "/member/:memberId/articles/category/:category",
    );
    const matchMemberCatalog = useRouteMatch<{
        catalogId: string;
        mode: "edit" | "create" | "view";
        context: CatalogContext;
    }>(routeSchemas.memberCatalog);
    const history = useHistory();

    const { globalState: panelState, setGlobalState: setPanelState } =
        useGlobalState<PanelUiState>(panelUiStateKey, {
            ...defaultPanelUiState,
            activeTab:
                isArticlesRoute || isArticleCategoriesRoute ? "articles" : "profile",
        });

    // Capped at one page, so this is not every active request of the member. Needs
    // real paging if members start going over it.
    const { data: memberRequests } = useMemberRequests(member.id);

    return (
        <>
            <div className="row">
                <div className="column">
                    <MemberHeader
                        member={member}
                        collapsed={panelState.isCollapsed}
                        toggleClicked={() => {
                            setPanelState({
                                ...panelState,
                                isCollapsed: !panelState.isCollapsed,
                            });
                        }}
                        showTopicFilter={false}
                    />
                    <div className="row">
                        <div className="tab request-tab">
                            <MemberRequests requests={memberRequests} member={member} />
                        </div>
                        <div className="tab">
                            {member && conversation && currentAgent && (
                                <Chat
                                    conversation={conversation}
                                    currentAgent={currentAgent}
                                    member={member}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {!panelState.isCollapsed && (
                    <MemberProfileHost
                        currentAgent={currentAgent}
                        memberId={member.id}
                    />
                )}
            </div>

            {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
            <Route path={routeSchemas.memberCatalogContext("proactivity")}>
                <FullScreenOverlay
                    bucket={({ size, minimize, maximize }) => (
                        <ProactivityCart
                            initialItems={getCurrentCatalogItems(
                                createContextKey(member, undefined),
                            )}
                            request={undefined}
                            member={member}
                            size={size}
                            minimize={minimize}
                            maximize={maximize}
                        />
                    )}
                >
                    <WithGoogle>
                        {(googlePlaceService) => (
                            <Catalog
                                currentAgent={currentAgent}
                                googlePlaceService={googlePlaceService}
                                suppliers={suppliers}
                                request={undefined}
                                member={member}
                                cartItems={getCurrentCatalogItems(
                                    createContextKey(member, undefined),
                                ).map((it) => it.id)}
                                standalone={false}
                            />
                        )}
                    </WithGoogle>
                </FullScreenOverlay>
            </Route>

            {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
            <Route path={routeSchemas.memberCatalogContext("browse")}>
                {matchMemberCatalog?.params.catalogId && (
                    <FullScreenOverlay>
                        <WithGoogle>
                            {(googlePlaceService) => (
                                <ViewCatalogItem
                                    catalogItemId={matchMemberCatalog.params.catalogId}
                                    currentAgent={currentAgent}
                                    googlePlaceService={googlePlaceService}
                                    suppliers={suppliers}
                                    onClose={() => {
                                        history.push(routes.member(member.id));
                                    }}
                                    onToggleArchive={() => {
                                        history.push(routes.member(member.id));
                                    }}
                                />
                            )}
                        </WithGoogle>
                    </FullScreenOverlay>
                )}
            </Route>
        </>
    );
};

export default MemberWithRequestListView;
